/**
 * Curated lucide-react icon registry. Replaces the `import * as LucideIcons`
 * pattern that defeated tree-shaking (and grew the icons chunk to 586 kB).
 *
 * Static imports here mean Vite can see exactly which icons are used and
 * tree-shake the rest. New icons referenced by admin-entered iconName values
 * must be added here — unknown names fall back to the resolver's default.
 *
 * If you find yourself adding more than ~80 icons, reconsider whether the use
 * case really needs runtime-string icon resolution or whether the call site
 * should just import the named icon directly.
 */
import { createElement, type ComponentType, type SVGProps } from 'react';
import {
  AtSign,
  Award,
  BadgeCheck,
  BarChart3,
  BookMarked,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  Camera,
  Circle,
  Clock,
  Coffee,
  Compass,
  Cpu,
  CreditCard,
  ExternalLink,
  Facebook,
  FileText,
  Flag,
  Github,
  Globe,
  GraduationCap,
  Hammer,
  Headphones,
  Heart,
  Image as ImageIcon,
  Instagram,
  Layers,
  Lightbulb,
  LineChart,
  Link as LinkIcon,
  Linkedin,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  Mic,
  Monitor,
  Phone,
  PieChart,
  Quote,
  Rocket,
  Rss,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  Smartphone,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Twitch,
  Twitter,
  Users,
  Video,
  Wrench,
  Youtube,
  Zap,
} from 'lucide-react';

export type LucideIconComponent = ComponentType<SVGProps<SVGSVGElement> & { className?: string; size?: number | string }>;

// lucide-react ships no X (formerly Twitter) brand glyph — its `X` export is a
// close-cross. This is the official X wordmark path, wrapped to match the
// lucide component signature (className/size forwarded). Built via createElement
// because this is a .ts file (no JSX).
const XBrand: LucideIconComponent = ({ size = 24, ...props }) =>
  createElement(
    'svg',
    { viewBox: '0 0 24 24', width: size, height: size, fill: 'currentColor', 'aria-hidden': true, ...props },
    createElement('path', {
      d: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    }),
  );

const ICON_REGISTRY: Record<string, LucideIconComponent> = {
  AtSign,
  Award,
  BadgeCheck,
  BarChart3,
  BookMarked,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  Camera,
  Circle,
  Clock,
  Coffee,
  Compass,
  Cpu,
  CreditCard,
  ExternalLink,
  Facebook,
  FileText,
  Flag,
  Github,
  Globe,
  GraduationCap,
  Hammer,
  Headphones,
  Heart,
  Image: ImageIcon,
  Instagram,
  Layers,
  Lightbulb,
  LineChart,
  Link: LinkIcon,
  Linkedin,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  Mic,
  Monitor,
  Phone,
  PieChart,
  Quote,
  Rocket,
  Rss,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  Smartphone,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Twitch,
  Twitter,
  Users,
  Video,
  Wrench,
  X: XBrand,
  Youtube,
  Zap,
};

export function getIcon(
  iconName: string | undefined | null,
  fallback: LucideIconComponent = Circle,
): LucideIconComponent {
  if (!iconName) return fallback;
  return ICON_REGISTRY[iconName] ?? fallback;
}

export const REGISTERED_ICON_NAMES: readonly string[] = Object.freeze(Object.keys(ICON_REGISTRY));
